/**
 * An instance: one person's onboarding or offboarding, made from a template.
 *
 * Materialising does four things and nothing else: picks the cards this
 * person's profile includes, re-joins the dependencies around the cards it
 * left out, works out every deadline, and puts each card in its first state.
 * From then on the instance belongs to `engine.ts`.
 *
 * The instance carries a copy of the template's nodes rather than a reference
 * to them. PRD 6.3: an in-flight instance is pinned to the version it was made
 * from, and the cheapest way to make that true is for there to be nothing to
 * un-pin.
 */

import {
  addBusinessDays,
  addCalendarDays,
  atTime,
  compareDates,
  dayAt,
  endOfDay,
  type ClockTime,
  type IsoDate,
} from "./dates.js";
import { topologicalOrder } from "./graph.js";
import { finalPayText } from "./finalpay.js";
import type { IssuedItem } from "./issued.js";
import type { SiteSettings } from "./settings.js";
import { choicesOf } from "./template.js";
import type {
  Condition,
  DaySpec,
  DeadlineSpec,
  EdgeKind,
  LifecycleKind,
  Profile,
  Template,
  TemplateNode,
} from "./template.js";

export type TaskState = "not_started" | "unblocked" | "in_progress" | "blocked" | "completed" | "skipped";

/** 0 none, 1 assignee reminded, 2 department head told, 3 HR's critical queue (PRD 7.4). */
export type EscalationLevel = 0 | 1 | 2 | 3;

/** One domain's handover note (PRD 9.4): what the leaver knows about it, and where it is written down. */
export interface Note {
  domain: string;
  text: string;
  link: string | null;
  by: string;
  at: number;
}

export interface Poke {
  by: string;
  at: number;
}

/** Something said on a card: a question, an answer, a heads-up. Never edited, never removed. */
export interface Comment {
  by: string;
  at: number;
  text: string;
  /** User ids the author named with @; each was told. */
  mentions: string[];
}

export interface Task {
  key: string;
  state: TaskState;
  department: string;
  assigneeId: string | null;
  backupId: string | null;
  /** The instant the task is overdue: end of its due day at the site, or the SLA's minute. */
  dueAt: number;
  /** The due day, for date-based deadlines; null for an SLA counted in minutes. */
  dueDay: IsoDate | null;
  /**
   * The template asked for a day that had already passed when the workflow
   * began -- "Day -14" on a departure five days away. The deadline is moved up
   * to the day it began and the card says so, rather than being born overdue.
   */
  compressed: boolean;
  subtasksDone: boolean[];
  /**
   * The checklist lines drawn from another card's choices (`checklistFrom`),
   * copied when the card opens, after the node's own `subtasks`. Kept on the
   * task so the lines ticked stay the lines shown. Absent on a card without
   * them, and on everything stored before they existed.
   */
  chosenLines?: string[];
  values: Record<string, string>;
  blockedReason: string | null;
  skipReason: string | null;
  escalation: EscalationLevel;
  pokes: Poke[];
  /** Present only on a card whose node takes notes; absent on every instance stored before they existed. */
  notes?: Note[];
  /** Absent on every instance stored before comments existed. */
  comments?: Comment[];
  startedAt: number | null;
  finishedAt: number | null;
  finishedBy: string | null;
  /** One entry per department in the card's `signoffs`: who signed and when, or null while still waiting. */
  signoffs: Record<string, { by: string; at: number } | null>;
  /**
   * The card does not open before this instant (the start of `notBeforeDay`
   * at the site), whatever it waits on. Absent on a card with no such day.
   */
  notBeforeAt?: number;
  notBeforeDay?: IsoDate;
}

export interface InstanceEdge {
  from: string;
  to: string;
  kind: EdgeKind;
}

export interface Instance {
  templateId: string;
  templateVersion: number;
  kind: LifecycleKind;
  profile: Profile;
  anchorDate: IsoDate;
  triggeredAt: number;
  timeZone: string;
  /**
   * When on the last day access is to go, on the site's clock, if somebody
   * asked for a time: "17:00". Cards that revoke access are due then rather
   * than at the end of the day. Absent on an onboarding, on a departure
   * nobody gave a time for, and on everything stored before it existed.
   */
  cutoffTime?: ClockTime;
  /** What the person's onboarding issued, copied here when a departure starts; the cards that collect it back check against it. */
  issued?: IssuedItem[];
  /** Cards in dependency order, which is also the order a queue lists them in. */
  order: string[];
  nodes: Record<string, TemplateNode>;
  edges: InstanceEdge[];
  tasks: Record<string, Task>;
  /**
   * Called off as a whole (`lifecycle.ts`): who, when and why. Absent on
   * every instance that was not, including all those stored before it existed.
   */
  cancelled?: Cancelled;
}

export interface Cancelled {
  at: number;
  by: string;
  reason: string;
}

export interface Assignee {
  primary: string | null;
  backup: string | null;
}

export interface MaterializeInput {
  profile: Profile;
  settings: SiteSettings;
  anchorDate: IsoDate;
  triggeredAt: number;
  /** Who does each department's work by default. A department with nobody is left unassigned and shows up for HR. */
  assignees: Record<string, Assignee>;
  /** A departure's requested access cut-off on the last day; null or absent when nobody asked for a time. */
  cutoffTime?: ClockTime | null;
  /** What the person's onboarding issued, for the departure's collecting cards to check against. */
  issued?: readonly IssuedItem[];
}

/** A department with nobody set to do its cards, and the cards that are left without an owner. */
export interface Unstaffed {
  department: string;
  cards: { key: string; title: string }[];
}

export interface Materialized {
  instance: Instance;
  /** Cards the profile left out, so a preview can say what this person will not be asked to do. */
  excluded: string[];
  /** Things a person should know before starting it, as sentences. */
  notes: string[];
  /** Departments with nobody to do their cards, in the order their first card comes. */
  unstaffed: Unstaffed[];
}

/**
 * Whether a condition holds for this person. A `recorded` condition is only
 * known once its card is finished, so it is tested with `recorded`, the value
 * that card recorded ("" for none); before then (`recorded` undefined) it is
 * taken to hold, and `settle()` asks again when the card's turn comes.
 */
export function conditionHolds(condition: Condition, profile: Profile, recorded?: string): boolean {
  let hit: boolean;
  if ("field" in condition) hit = condition.values.includes(profile[condition.field] ?? "");
  else if ("question" in condition) {
    const answer = profile.answers?.[condition.question];
    hit = answer !== undefined && condition.values.includes(answer);
  } else {
    if (recorded === undefined) return true;
    // A multiple-choice value holds when any one of its choices is listed.
    const chosen = choicesOf(recorded);
    hit = chosen.length
      ? chosen.some((value) => condition.values.includes(value))
      : condition.values.includes("");
  }
  return condition.op === "in" ? hit : !hit;
}

/** Whether the profile keeps this card. Conditions on a recorded value are decided later, by `settle()`. */
export function nodeApplies(node: TemplateNode, profile: Profile): boolean {
  return (node.conditions ?? []).every((condition) => conditionHolds(condition, profile));
}

/** The conditions on a card that wait for a value an earlier card records. */
export const recordedConditions = (node: TemplateNode) =>
  (node.conditions ?? []).filter(
    (condition): condition is Extract<Condition, { recorded: unknown }> => "recorded" in condition,
  );

/**
 * The value a recorded condition tests, once its card is finished: what was
 * recorded, or "" when it was skipped or left blank. Undefined while the card
 * is still to be done.
 */
export function recordedValue(
  instance: Pick<Instance, "tasks">,
  link: { node: string; field: string },
): string | undefined {
  const source = instance.tasks[link.node];
  if (!source) return "";
  if (source.state === "completed") return source.values[link.field] ?? "";
  return source.state === "skipped" ? "" : undefined;
}

/** The first moment of the day a card may open on, or null when it has no such day. */
export function notBeforeFor(
  spec: DaySpec | undefined,
  input: { settings: SiteSettings; anchorDate: IsoDate; triggeredAt: number },
): { notBeforeAt: number; notBeforeDay: IsoDate } | null {
  if (!spec) return null;
  const base = spec.from === "anchor" ? input.anchorDate : dayAt(input.triggeredAt, input.settings.timeZone);
  const unit = spec.unit ?? input.settings.deadlineUnit;
  const day =
    unit === "calendar"
      ? addCalendarDays(base, spec.days)
      : addBusinessDays(base, spec.days, input.settings.calendar);
  return { notBeforeAt: atTime(day, "00:00", input.settings.timeZone), notBeforeDay: day };
}

/**
 * The edges among the kept cards, with every path through a dropped card
 * joined up again.
 *
 * Without this, leaving out the middle of A -> B -> C would let C start before
 * A -- the warehouse branch removes "workstation prepared", and whatever came
 * after it would lose its wait on whatever came before. A bridged edge is hard
 * only when every edge on the path was hard: one soft link anywhere means the
 * template's author already allowed the later card to go early.
 */
export function bridge(kept: ReadonlySet<string>, edges: readonly InstanceEdge[]): InstanceEdge[] {
  const out = new Map<string, InstanceEdge>();
  const add = (edge: InstanceEdge) => {
    const id = `${edge.from}\u0000${edge.to}`;
    const existing = out.get(id);
    // Two routes to the same pair: hard wins, because one of them says wait.
    if (!existing || (existing.kind === "soft" && edge.kind === "hard")) out.set(id, edge);
  };

  const from = new Map<string, InstanceEdge[]>();
  for (const edge of edges) from.set(edge.from, [...(from.get(edge.from) ?? []), edge]);

  for (const start of kept) {
    // Walk forward through dropped cards only, remembering whether the path is still all hard.
    const stack: { at: string; hard: boolean }[] = [{ at: start, hard: true }];
    const seen = new Set<string>();
    while (stack.length > 0) {
      const { at, hard } = stack.pop()!;
      for (const edge of from.get(at) ?? []) {
        const stillHard = hard && edge.kind === "hard";
        if (kept.has(edge.to)) {
          add({ from: start, to: edge.to, kind: stillHard ? "hard" : "soft" });
          continue;
        }
        const id = `${edge.to}:${stillHard}`;
        if (seen.has(id)) continue;
        seen.add(id);
        stack.push({ at: edge.to, hard: stillHard });
      }
    }
  }
  return [...out.values()];
}

/**
 * When a deadline falls, and whether it had to be moved up to the day the
 * workflow began. `time` puts a deadline counted in days at that time of its
 * day rather than at the end of it: a card that revokes access, on a
 * departure that asked for a cut-off (`timeFor()`). A time already past is
 * left past: the card is late against what was asked, and says so.
 */
export function deadlineFor(
  spec: DeadlineSpec,
  input: { settings: SiteSettings; anchorDate: IsoDate; triggeredAt: number; time?: ClockTime | null },
): { dueAt: number; dueDay: IsoDate | null; compressed: boolean } {
  const { settings, anchorDate, triggeredAt, time } = input;
  if ("minutes" in spec)
    return { dueAt: triggeredAt + spec.minutes * 60_000, dueDay: null, compressed: false };

  const triggerDay = dayAt(triggeredAt, settings.timeZone);
  const base = spec.from === "anchor" ? anchorDate : triggerDay;
  const unit = spec.unit ?? settings.deadlineUnit;
  let day =
    unit === "calendar"
      ? addCalendarDays(base, spec.days)
      : addBusinessDays(base, spec.days, settings.calendar);

  let compressed = false;
  if (compareDates(day, triggerDay) < 0) {
    day = triggerDay;
    compressed = true;
  }
  const dueAt = time ? atTime(day, time, settings.timeZone) : endOfDay(day, settings.timeZone);
  return { dueAt, dueDay: day, compressed };
}

/** The time of day a card's deadline falls at, when it is not the end of the day. */
export const timeFor = (node: TemplateNode, cutoffTime: ClockTime | null | undefined): ClockTime | null =>
  node.revokesAccess && cutoffTime ? cutoffTime : null;

/**
 * Whether a template can take a cut-off time: it has a card that revokes
 * access with a deadline counted in days. A termination's are counted in
 * minutes from the moment it starts, so there is no time to choose.
 */
export const takesCutoff = (template: Pick<Template, "nodes">): boolean =>
  template.nodes.some((node) => node.revokesAccess && !("minutes" in node.deadline));

/** When access was asked to go, and when it went, for one card that revokes it. */
export interface AccessTime {
  task: string;
  title: string;
  /**
   * The moment asked for: the cut-off, or the end of the day when nobody gave
   * a time -- or, for an SLA counted in minutes, the moment the departure
   * began, because a termination asks for it at once.
   */
  requestedAt: number;
  /** Null until it is done; a skipped card was never done. */
  doneAt: number | null;
}

/**
 * The access comparison a client's offboarding form makes on paper: the
 * time asked for beside the time it was done, per card that revokes access.
 * Read in the instance's own zone by whoever shows it (`timeZone`).
 */
export function accessTimes(instance: Instance): AccessTime[] {
  return instance.order.flatMap((key) => {
    const node = instance.nodes[key]!;
    const task = instance.tasks[key]!;
    if (!node.revokesAccess) return [];
    return [
      {
        task: key,
        title: node.title,
        requestedAt: "minutes" in node.deadline ? instance.triggeredAt : task.dueAt,
        doneAt: task.state === "completed" ? task.finishedAt : null,
      },
    ];
  });
}

/** The cut-off already passed at the site when the departure starts, as a sentence; null when it has not. */
export function cutoffProblem(instance: Instance, now: number): string | null {
  if (!instance.cutoffTime) return null;
  const late = accessTimes(instance).some((access) => access.requestedAt < now);
  return late
    ? `${instance.cutoffTime} on ${instance.anchorDate} has already passed at the site. Choose a later time, or leave it blank for the end of the day.`
    : null;
}

/**
 * A final-pay card says what the law says where the site is, or says it does
 * not know and what to do about it: the rule is only useful to HR if it is on
 * the card they are looking at, and a card that quoted nothing would send them
 * to search for it.
 */
function finalPayDescription(node: TemplateNode, input: MaterializeInput, notes: string[]): string {
  const rule = input.settings.jurisdiction
    ? finalPayText(input.settings.jurisdiction, input.profile.trigger)
    : null;
  if (rule) return [node.description, rule].filter(Boolean).join(" ");
  notes.push("Set the site's state in Settings and the final pay card will say what the law there requires.");
  return node.description ?? "";
}

export function materialize(template: Template, input: MaterializeInput): Materialized {
  const byProfile = new Set(
    template.nodes.filter((node) => nodeApplies(node, input.profile)).map((node) => node.key),
  );
  // A card branching on a value from a card this person does not get is
  // decided now: nothing will ever be recorded there, so the value is "".
  const keptNodes = template.nodes.filter(
    (node) =>
      byProfile.has(node.key) &&
      recordedConditions(node).every(
        (condition) => byProfile.has(condition.recorded.node) || conditionHolds(condition, input.profile, ""),
      ),
  );
  const kept = new Set(keptNodes.map((node) => node.key));
  const excluded = template.nodes.filter((node) => !kept.has(node.key)).map((node) => node.key);
  const notes: string[] = [];
  const unstaffed: Unstaffed[] = [];

  const nodes: Record<string, TemplateNode> = {};
  for (const node of keptNodes) {
    // An input whose source card this person does not get can never be filled
    // in, so keeping it would lock the reading card for ever. It is dropped,
    // and said so.
    const inputs = (node.inputs ?? []).filter((link) => kept.has(link.node));
    if (inputs.length !== (node.inputs ?? []).length) {
      notes.push(`"${node.title}" will not show a value from a card this person is not getting.`);
    }
    nodes[node.key] = {
      ...node,
      inputs,
      // Decided already, so not asked again at its turn.
      ...(node.conditions
        ? {
            conditions: node.conditions.filter(
              (condition) => !("recorded" in condition) || kept.has(condition.recorded.node),
            ),
          }
        : {}),
      ...(node.finalPay ? { description: finalPayDescription(node, input, notes) } : {}),
    };
  }

  // A card branching on a recorded value waits for the card that records it,
  // as a hard dependency: it cannot be decided before then. So does a card
  // whose checklist is the choices made there.
  const branchEdges: InstanceEdge[] = template.nodes.flatMap((node) => [
    ...recordedConditions(node).map((condition) => ({
      from: condition.recorded.node,
      to: node.key,
      kind: "hard" as const,
    })),
    ...(node.checklistFrom ? [{ from: node.checklistFrom.node, to: node.key, kind: "hard" as const }] : []),
  ]);
  const edges = bridge(kept, [...template.edges, ...branchEdges]);
  const order = topologicalOrder(
    keptNodes.map((node) => node.key),
    [
      ...edges,
      ...keptNodes.flatMap((node) =>
        (nodes[node.key]?.inputs ?? []).map((link) => ({ from: link.node, to: node.key })),
      ),
    ],
  );

  const tasks: Record<string, Task> = {};
  for (const node of keptNodes) {
    const deadline = deadlineFor(node.deadline, { ...input, time: timeFor(node, input.cutoffTime) });
    if (deadline.compressed)
      notes.push(`"${node.title}" was due before this started, so it is due today instead.`);
    const who = input.assignees[node.department] ?? { primary: null, backup: null };
    if (!who.primary) {
      const card = { key: node.key, title: node.title };
      const group = unstaffed.find((entry) => entry.department === node.department);
      if (group) group.cards.push(card);
      else unstaffed.push({ department: node.department, cards: [card] });
    }

    const notBefore = notBeforeFor(node.notBefore, input);
    tasks[node.key] = {
      ...(notBefore ?? {}),
      key: node.key,
      state: "not_started",
      department: node.department,
      assigneeId: who.primary,
      backupId: who.backup,
      ...deadline,
      subtasksDone: (node.subtasks ?? []).map(() => false),
      values: {},
      blockedReason: null,
      skipReason: null,
      escalation: 0,
      pokes: [],
      ...(node.notes ? { notes: [] } : {}),
      startedAt: null,
      finishedAt: null,
      finishedBy: null,
      signoffs: Object.fromEntries((node.signoffs ?? []).map((department) => [department, null])),
    };
  }

  return {
    instance: {
      templateId: template.id,
      templateVersion: template.version,
      kind: template.kind,
      profile: input.profile,
      anchorDate: input.anchorDate,
      triggeredAt: input.triggeredAt,
      timeZone: input.settings.timeZone,
      ...(input.cutoffTime ? { cutoffTime: input.cutoffTime } : {}),
      ...(input.issued?.length ? { issued: [...input.issued] } : {}),
      order,
      nodes,
      edges,
      tasks,
    },
    excluded,
    notes,
    unstaffed,
  };
}

/** A card's checklist: its own lines, then any drawn from another card's choices when it opened. */
export const checklistOf = (node: TemplateNode, task: Pick<Task, "chosenLines">): string[] => [
  ...(node.subtasks ?? []),
  ...(task.chosenLines ?? []),
];
